import { defineStore } from 'pinia';
import global from '@/global';
import { useAuthStore } from './auth';

export const useOnboardingStore = defineStore('onboarding', {
  state: () => ({
    routeStatuses: null,
    isLoading: false
  }),

  actions: {
    async fetchRouteStatuses() {
      const authStore = useAuthStore();
      this.isLoading = true;
      
      try {
        const response = await fetch(`${global.API_URL}/onboarding/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          // Handle the nested structure properly
          if (data && data.step_statuses) {
            // Check if the steps are nested under 'steps' key
            if (data.step_statuses.steps) {
              this.routeStatuses = data.step_statuses.steps;
            } else {
              // If not nested, use the step_statuses directly
              // Filter out any non-route keys that might be present
              this.routeStatuses = {};
              Object.keys(data.step_statuses).forEach(key => {
                if (key.startsWith('/onboarding/')) {
                  this.routeStatuses[key] = data.step_statuses[key];
                }
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch onboarding status:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async batchUpdateRouteStatus({ updates }) {
      if (!this.routeStatuses) return;
      
      // Track if any actual updates were made
      let updatesNeeded = false;
      
      updates.forEach(({ routeName, newRouteState }) => {
        // Don't update if the route is already completed (status === 1)
        if (this.routeStatuses[routeName] !== 1) {
          // Check if the value is actually changing
          if (this.routeStatuses[routeName] !== newRouteState) {
            this.routeStatuses[routeName] = newRouteState;
            updatesNeeded = true;
          }
        }
      });
      
      // Only call API if we actually made changes
      if (updatesNeeded) {
        await this.saveToAPI();
      }
    },

    async saveToAPI() {
      if (!this.routeStatuses) return;
      
      const authStore = useAuthStore();
      try {
        await fetch(`${global.API_URL}/onboarding/status`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            step_statuses: {
              steps: this.routeStatuses
            }
          })
        });
      } catch (error) {
        console.error('Failed to update onboarding status:', error);
      }
    },

    getRouteStatus(routeName) {
      return this.routeStatuses?.[routeName] ?? -1;
    },

    isRouteAccessible(routeName) {
      return this.getRouteStatus(routeName) >= 0;
    },

    forceChangeRouteState({ routeName, newRouteState }) {
      if (!this.routeStatuses) return;
      
      // Only update if the value is actually changing
      if (this.routeStatuses[routeName] !== newRouteState) {
        this.routeStatuses[routeName] = newRouteState;
        this.saveToAPI();
      }
    }
  }
});